import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';

import intl from 'react-intl-universal';
import { TaskTimer } from 'tasktimer';
import Grid from '@mui/material/Grid2';
import Autocomplete from '@mui/material/Autocomplete';
// components and parts
import { Typography, Box } from '@mui/material';
import MDButton from 'components/MDButton';
import useApi from 'shared/hooks/api';
import localData from 'utils/localData';
import DoCloudDropzone from 'components/DropZone';
import { PageLoader, PageError, TextEditor } from 'components';
import { useMaterialUIController } from 'context';
import {
  useUploadMissionsContextController,
  addNewMission,
  setMissionStatusContext,
  contextUpdateUploadingSpeed,
} from 'context/NewIssueContext';
import { MISSION_CATEGORY } from 'context/constants';

import {
  initNewKenshinIssue,
  uploadBatchAttachmentsToS3,
  checkForAttachmentsCompletionOnNewIssue,
  getDstOrgOptions,
  getEmergencyOptions,
  getDstOrgObjectById,
  generateConfirmWindowContents,
  BACKEND_DESTINATION,
} from 'src/views/New/utils/utils';

import { calcBatchTotalSize } from 'utils/fileHandling';

import pxToRem from 'assets/theme/functions/pxToRem';
import theme from 'assets/theme';
import themeDark from 'assets/theme-dark';
import { formConstants } from '../constants/constants';
import { DOLPHIN_API } from 'shared/constants/apis';

import AutoCompleteFormField from 'components/Form/AutocompleteFormField';
import NewIssueModal from '../components/modals/NewIssueModal';
import ExceededLimitModal from '../components/modals/ExceededLimitModal';
import ConfirmIssueModal from '../components/modals/ConfirmIssueModal';
import SentIssueFailedModal from '../components/modals/SentIssueFailedModal';
import logger from 'utils/logger';
import { ConfirmModal } from 'shared/modals/confirmModal';
import { USER_DATA } from 'shared/constants/users';

const propTypes = {
  close: PropTypes.func,
};

// Create a logger instance for this component
const log = logger.createLogger('NewKenshin');

const DashboardNewKenshin = ({ close }) => {
  const initDescription = '';

  const [controllerUploadMissions, dispatchUploadMissions] =
    useUploadMissionsContextController();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const currentOrganization = localData.get(USER_DATA.CURRENT_ORGANIZATION);
  const currentOrganizationId = localData.get(
    USER_DATA.CURRENT_ORGANIZATION_ID
  );

  // states specifically for resetting some Autocomplete inputs;
  const [toggleResetEmergency, setToggleResetEmergency] = useState(false);
  const [toggleResetDstOrgName, setToggleResetDstOrgName] = useState(false);
  const [toggleResetDescription, setToggleResetDescription] = useState(false);

  // states related to pre-sending;
  const [exceedUploadMissionsLimit, setExceedUploadMissionsLimit] =
    useState(false);
  const [warnFormIncomplete, setWarnFormIncomplete] = useState(false);
  const [incompleteFields, setIncompleteFields] = useState([]);
  const [showWarningBeforeSend, setShowWarningBeforeSend] = useState(false); // show modal window to confirm sending new Issue;
  const [sendContentsConfirmed, setSendContentsConfirmed] = useState(false); // whether the confirmation checkbox is clicked in above modal window;
  const [newIssueConfirmButtonIsLoading, setNewIssueConfirmButtonIsLoading] =
    useState(false); // whether the 'confirm' button in above modal window is clicked or not;

  const [checkboxForm, setCheckboxForm] = useState([]);
  const [tagListJson] = useState({});

  const [selectedjson] = useState([]);

  const [formState, setFormState] = useState({
    srcOrgName: currentOrganization,
    srcOrgId: currentOrganizationId,
    dstOrgName: '',
    dstOrgId: '',
    emergency: '',
    description: initDescription,
    file: '',
    dataSet: '',
    doctorsInCharge: [],
    doctorIdsFromSrcOrg: [],
  });

  const [structuredAttachments, setStructuredAttachments] = useState([]);
  // states related to procress of sending new issue;

  const [confirmNotCheck, setconfirmNotCheck] = useState(false);
  const [sendNewIssueFailed, setSendNewIssueFailed] = useState(false);
  const [, setDoctorsInCharge] = useState([]);

  useEffect(() => {
    const id = controllerUploadMissions.newIssueIdentifierId;
    if (id in controllerUploadMissions.missions) {
      const { status, missionCategory } = controllerUploadMissions.missions[id];
      if (missionCategory === MISSION_CATEGORY.KENSHIN && status === 'INIT') {
        resetForm();
        const missionId = controllerUploadMissions.newIssueIdentifierId;
        const mission = controllerUploadMissions.missions[missionId];
        sendNewIssueContinued(mission);
      }
    }
  }, [controllerUploadMissions.newIssueIdentifierId]);

  const [{ data, error }] = useApi.useGet(DOLPHIN_API.ORGANIZATION_NEW, {
    params: { organizationId: currentOrganizationId },
  });

  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const { dstOrgs } = data;

  dstOrgs.sort((a, b) => {
    return a.name < b.name ? -1 : 1;
  });
  const resetForm = () => {
    setFormState({
      srcOrgName: currentOrganization,
      srcOrgId: currentOrganizationId,
      dstOrgName: '',
      dstOrgId: '',
      emergency: '',
      studyID: '',
      bodyPart: '',
      modality: '',
      description: '',
      file: '',
      dataSet: '',
      doctorsInCharge: [],
      doctorIdsFromSrcOrg: [],
    });
    setDoctorsInCharge([]);

    setToggleResetEmergency(!toggleResetEmergency);
    setToggleResetDstOrgName(!toggleResetDstOrgName);
    setToggleResetDescription(!toggleResetDescription);
    setCheckboxForm([]);

    setStructuredAttachments([]);

    if (close !== null && close !== undefined) {
      close();
    }
  };

  // Handle changes from destOrgId
  const handleChangeOnForm = (key, value) => {
    const newFormState = {
      ...formState,
      [key]: value && value.value ? value.value : '',
    };
    // special case
    if (value && value.value) {
      if (key === formConstants.DEST_ORG_ID) {
        const dstOrg = getDstOrgObjectById(value.value, dstOrgs);
        newFormState.dstOrgName = dstOrg.name.toString();
      }
    }
    setFormState({ ...newFormState });
  };

  const handleDescriptionChange = (htmlValue) => {
    setFormState((prevFormState) => ({
      ...prevFormState,
      description: htmlValue,
    }));
  };

  const checkIfFormCompleted = () => {
    if (!formState.dstOrgId || !formState.emergency) {
      let incompleteFields = [
        !formState.dstOrgId ? intl.get('new_label_send_to') : null,
        !formState.emergency ? intl.get('new_label_emergency') : null,
      ];
      incompleteFields = incompleteFields.filter(function (e) {
        return e;
      });
      setIncompleteFields(incompleteFields);
      return false;
    } else {
      return true;
    }
  };

  const handleConfirmContentsCheckbox = () => {
    if (sendContentsConfirmed) {
      setSendContentsConfirmed(false);
    } else {
      setSendContentsConfirmed(true);
    }
  };

  const handleSubmitClick = async (event) => {
    event.preventDefault();
    log.debug('handleSubmitClick');
    if (checkIfFormCompleted()) {
      log.debug('checkIfFormCompleted');
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      setShowWarningBeforeSend(true);
      return;
    } else {
      log.debug('checkIfFormCompleted false');
      setWarnFormIncomplete(true);
      return;
    }
  };

  const handleShowWarningBeforeSendModalClose = () => {
    setSendContentsConfirmed(false);
    setShowWarningBeforeSend(false);
  };

  const handleConfirmNotCheckModalClose = () => {
    setconfirmNotCheck(false);
  };

  const handleNewIssueFailModalClose = () => {
    setNewIssueConfirmButtonIsLoading(false);
    setShowWarningBeforeSend(false);
    setSendContentsConfirmed(false);
    setSendNewIssueFailed(false);
  };

  const handleExceedUploadMissionsLimitClose = () => {
    setExceedUploadMissionsLimit(false);
  };

  const getNumOngoingUploads = () => {
    const ongoings = Object.values(controllerUploadMissions.missions).filter(
      (x) => x.status === 'UPLOADING' || x.status === 'PROCESSING'
    );
    return ongoings.length;
  };

  const sendNewIssue = async () => {
    if (!sendContentsConfirmed) {
      setconfirmNotCheck(true);
      return false;
    }

    // Step 0: check if there are already 3 ongoing uploads missions;
    if (getNumOngoingUploads() >= 3) {
      setExceedUploadMissionsLimit(true);
      return;
    }

    setNewIssueConfirmButtonIsLoading(true);

    // Step 1: Create the Issue Entity in the backend, and get the issue unique ID;
    let issueInfo;
    try {
      issueInfo = await initNewKenshinIssue(
        formState,
        checkboxForm,
        structuredAttachments,
        tagListJson
      );
    } catch (error) {
      resetForm();
      setSendNewIssueFailed(true);
      log.error('Error in sendNewIssue', { error });
      return false;
    }

    const { issueId, issueIdentifierId, issueCreatedAt } = issueInfo;

    // Add the upload mission to Context;
    const batchTotalBytes = calcBatchTotalSize(structuredAttachments);

    addNewMission(dispatchUploadMissions, {
      issueId,
      issueIdentifierId,
      issueCreatedAt,
      formState,
      checkboxForm,
      structuredAttachments,
      batchTotalBytes,
      tagListJson: {},
      missionCategory: MISSION_CATEGORY.KENSHIN,
    });

    setNewIssueConfirmButtonIsLoading(false);
    setShowWarningBeforeSend(false);
    setSendContentsConfirmed(false);
    // The rest of the steps are in function "sendNewIssueContinued"
    // It has to wait for addNewMission to finish updating the state stored in context;
    // Therefore It is placed inside useEffect hook
  };

  const sendNewIssueContinued = async (mission) => {
    // This function is a continuation from the "sendNewIssue" function;
    // It is placed in useEffect hook above and only executed after context state is successfully updated;

    const {
      issueId,
      issueIdentifierId,
      issueCreatedAt,
      formState,
      structuredAttachments,
      batchTotalBytes,
    } = mission;

    // Step 1: start a timer to track the upload speed of the upload mission that is about to start;
    const timer0 = new TaskTimer(1000); // milliseconds
    const task_uploadSpeed_checker = {
      id: 'heartbeat',
      tickInterval: 2, // number of ticks between 2 task runs;
      async callback() {
        // can also be an async function, returning a promise
        contextUpdateUploadingSpeed(dispatchUploadMissions, {
          missionId: issueIdentifierId,
        });
        if (!['INIT', 'UPLOADING'].includes(mission.status)) {
          timer0.stop();
        }
      },
    };
    timer0.add(task_uploadSpeed_checker).start();

    // Step 2: Upload the attachments to S3 bucket, and notify the backend to process it;
    try {
      setMissionStatusContext(dispatchUploadMissions, {
        missionId: issueIdentifierId,
        value: 'UPLOADING',
      });
      await uploadBatchAttachmentsToS3({
        missionId: issueIdentifierId,
        issueId,
        issueIdentifierId,
        issueCreatedAt,
        structuredAttachments,
        batchTotalBytes,
        srcOrgId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        dstOrgId: formState.dstOrgId,
        controllerUploadMissions,
        dispatchUploadMissions,
        destination: BACKEND_DESTINATION.KENSHIN,
      });
    } catch (error) {
      // if cancelled, error = {message: 'canceled', name: 'CanceledError', code: 'ERR_CANCELED'}
      if (error.code === 'ERR_CANCELED') {
        log.error('Upload is aborted', { error });
        setMissionStatusContext(dispatchUploadMissions, {
          missionId: issueIdentifierId,
          value: 'UPLOAD_ABORTED',
        });
      }
      return false;
    }

    setMissionStatusContext(dispatchUploadMissions, {
      missionId: issueIdentifierId,
      value: 'PROCESSING',
    });

    // not necessary for the temp hack solution for now;
    // Step 3: Notify the backend to start a periodic check on this issue;
    // await startIssuePeriodicStatusCheck(issueId);

    // Step 4: while waiting for backend to process, poll every a few seconds to check for new status;
    const timer = new TaskTimer(1000); // milliseconds
    const task1 = {
      id: 'heartbeat',
      tickInterval: 5, // number of ticks between 2 taskTuns; e.g. run every 5 sec;
      totalRuns: 1440, // times to run; max 2 hour before timeout;
      async callback() {
        // can also be an async function, returning a promise
        const issueStatusRes = await checkForAttachmentsCompletionOnNewIssue(
          issueId,
          'kenshin'
        );
        if (issueStatusRes === 'Complete') {
          setMissionStatusContext(dispatchUploadMissions, {
            missionId: issueIdentifierId,
            value: 'PROCESS_COMPLETE',
          });
          timer.stop();
        }
        if (issueStatusRes === 'Failed') {
          setMissionStatusContext(dispatchUploadMissions, {
            missionId: issueIdentifierId,
            value: 'PROCESS_FAILED',
          });
          timer.stop();
        }
      },
    };
    timer.add(task1).start();
  };

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <div data-testid="page-width-control" style={{ minWidth: '980px' }}>
        <Grid
          data-testid="page-new-kenshin-grid-container"
          spacing={3}
          container
          style={{
            paddingLeft: 100,
            marginTop: 30,
            paddingRight: 30,
          }}>
          <Grid size={12}>
            <Typography variant="h3">
              {intl.get('new_kenshin_text_new_task')}
            </Typography>
          </Grid>
          <Grid size={6} style={{ marginTop: 10 }}>
            <Autocomplete
              data-testid="new-issue-select-destination"
              id="dstOrgName"
              onChange={(event, newValue) => {
                handleChangeOnForm(formConstants.DEST_ORG_ID, newValue);
              }}
              options={getDstOrgOptions(dstOrgs)}
              isOptionEqualToValue={(option, value) =>
                option.label === value.label
              }
              renderInput={(params) => (
                <AutoCompleteFormField
                  {...params}
                  label={intl.get('new_label_send_to')}
                  placeholder={intl.get('new_helper_send_to')}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              )}
              key={toggleResetDstOrgName}
            />
          </Grid>
          <Grid size={6} style={{ marginTop: 10 }}>
            <Autocomplete
              data-testid="new-issue-select-emergency"
              id="emergency"
              onChange={(event, newValue) => {
                handleChangeOnForm(formConstants.EMERGENCY, newValue);
              }}
              options={getEmergencyOptions(BACKEND_DESTINATION.KENSHIN)}
              isOptionEqualToValue={(option, value) =>
                option.label === value.label
              }
              renderInput={(params) => (
                <AutoCompleteFormField
                  {...params}
                  label={intl.get('new_label_emergency')}
                  placeholder={intl.get('new_helper_emergency')}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              )}
              key={toggleResetEmergency}
            />
          </Grid>

          <Grid size={12}>
            <Typography
              style={{
                fontWeight: 'light',
                fontSize: '12px',
                color: 'light',
                opacity: 0.7,
                marginBottom: '10px',
                marginTop: 0,
              }}>
              {intl.get('new_label_additional_info')}
            </Typography>
            <TextEditor
              sx={{
                backgroundColor: '#fafafa',
                height: 185,
              }}
              placeholder={intl.get('new_label_case_description')}
              defaultValue={formState.description}
              onChange={handleDescriptionChange}
              key={toggleResetDescription}
            />
          </Grid>

          {/* Dropzone */}
          <Grid size={12} style={{ marginTop: 30 }}>
            <div style={{ position: 'relative' }}>
              <DoCloudDropzone
                existingStructuredAttachments={[]}
                structuredAttachments={structuredAttachments}
                setStructuredAttachments={setStructuredAttachments}
                darkMode={darkMode}
                isKenshin={true}
              />
            </div>
          </Grid>
          <Grid
            size={12}
            style={{
              marginTop: '45px',
              display: 'flex',
              justifyContent: 'center',
            }}>
            <MDButton
              data-testid="new_send_button"
              color="tertiary"
              variant="contained"
              onClick={handleSubmitClick}
              style={{
                fontSize: pxToRem(18),
                minWidth: '300px',
                borderRadius: '30px',
              }}>
              {intl.get('new_button_submit')}
            </MDButton>
          </Grid>
        </Grid>

        <SentIssueFailedModal
          isOpened={sendNewIssueFailed}
          onConfirm={handleNewIssueFailModalClose}
        />

        <ConfirmIssueModal
          isOpened={confirmNotCheck}
          onConfirm={handleConfirmNotCheckModalClose}
        />

        <ExceededLimitModal
          exceedUploadMissionsLimit={exceedUploadMissionsLimit}
          handleExceedUploadMissionsLimitClose={
            handleExceedUploadMissionsLimitClose
          }
        />

        <ConfirmModal
          isOpened={warnFormIncomplete}
          title={intl.get('new_modal_text_warning_title')}
          bodyText={intl.get('new_modal_text_warning_text_empty')}
          onConfirm={() => setWarnFormIncomplete(false)}
          confirmButtonText={intl.get('new_modal_tag_confirm')}>
          <Box>
            {incompleteFields &&
              incompleteFields.map((field, index) => (
                <Typography variant="h6" style={{ color: 'red' }} key={index}>
                  {field}
                </Typography>
              ))}
            <Typography variant="h6" style={{ paddingTop: '30px' }}>
              {intl.get('new_modal_text_warning_text_fill')}
            </Typography>
          </Box>
        </ConfirmModal>

        <NewIssueModal
          showWarningBeforeSend={showWarningBeforeSend}
          sendNewIssue={sendNewIssue}
          handleShowWarningBeforeSendModalClose={
            handleShowWarningBeforeSendModalClose
          }
          newIssueConfirmButtonIsLoading={newIssueConfirmButtonIsLoading}
          windowContents={generateConfirmWindowContents(
            formState,
            structuredAttachments,
            darkMode,
            selectedjson,
            {},
            true
          )}
          darkMode={darkMode}
          handleConfirmContentsCheckbox={handleConfirmContentsCheckbox}
        />
      </div>
    </ThemeProvider>
  );
};

DashboardNewKenshin.propTypes = propTypes;

export default DashboardNewKenshin;
